import { areaCentre, hasLowerLevel, levelAt, mapInfo, siteBox, worldToRadar, type Level, type MapId } from "@69ly/maps";
import { importanceRank, planPoints, type Area, type Combo, type LiveMember, type Side, type Site, type Stuff } from "@69ly/shared";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { errorText } from "../../lib/errors.ts";
import { useTracked, useTrackers } from "../../lib/live.ts";
import { emptyPlan, floorOf, usePlanSchedule } from "../../lib/plan.ts";
import { useStuffs } from "../../lib/stuff-queries.ts";
import { useCombos, useExecutions, usePlantSpots } from "../../lib/teamplay-queries.ts";
import { useActorNames } from "../../pages/Executions.tsx";
import { ComboGlyphs, ComboOnMap, comboSpot, defaultComboShow, type ComboShow } from "../ComboBits.tsx";
import { GrenadeIcon, GrenadeMark, MarkerIcon } from "../MarkerIcon.tsx";
import { StackedMarkers, type MapItem } from "../MarkerStack.tsx";
import { PlanStage } from "../plan/PlanStage.tsx";
import { PlantOnMap, SiteOutline, type PlantLayers } from "../PlantBits.tsx";
import { focusOn, focusOnBox, OnMap, Radar, ThrowLine, type RadarFocus } from "../Radar.tsx";
import { ErrorState, Loading } from "../Status.tsx";
import { BombStrip, ComboCard, ComboView, ExecCard, execRow, ExecView, PlantCard, PlantView, StuffCard, StuffView, type ExecRow } from "./LiveParts.tsx";
import { LiveSheet, type SheetSnap } from "./LiveSheet.tsx";

export type AreaFilter = "all" | "a" | "b" | "mid";
type Tab = "singles" | "combos" | "executions";
type Open = { kind: "stuff" | "combo" | "exec" | "plant"; id: string } | null;

interface Single {
  stuff: Stuff;
  by: string[];
}
interface ComboItem {
  combo: Combo;
  by: string[];
}
interface LiveMarker extends MapItem {
  stuff?: Stuff;
  combo?: Combo;
  by: string[];
}

const AREA_ORDER: Area[] = ["a", "mid", "b", "other"];
const EMPTY_PLAN = emptyPlan("execute");
const NO_STUFFS: Stuff[] = [];
const NO_COMBOS: Combo[] = [];
const ALL_LAYERS: PlantLayers = { utility: true, positions: true, cones: true };
const initial = (name: string) => name.trim().charAt(0).toUpperCase() || "?";
const stuffLevel = (map: MapId, s: Stuff): Level => levelAt(map, s.landing.z ?? s.throw.z);

function areaFocus(map: MapId, area: AreaFilter): RadarFocus | undefined {
  if (area === "a" || area === "b") {
    const b = siteBox(map, area);
    return b ? focusOnBox({ u0: b.u0 - 0.08, v0: b.v0 - 0.08, u1: b.u1 + 0.08, v1: b.v1 + 0.08 }, { pad: 0, maxScale: 2.6 }) : undefined;
  }
  if (area === "mid") {
    const c = areaCentre(map, "mid");
    return c ? { u: c.u, v: c.v, scale: 1.8 } : undefined;
  }
  return undefined;
}

/** Phones and narrow windows: the map on top, the panel in a sheet under it. */
const NARROW = "(max-width: 959px)";
function useNarrow() {
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && !!window.matchMedia?.(NARROW).matches);
  useEffect(() => {
    const m = window.matchMedia?.(NARROW);
    if (!m) return;
    const on = () => setNarrow(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return narrow;
}

/** The board fills the window from where it starts, down to the bottom: nothing to scroll in a game. */
function useFill(ref: RefObject<HTMLElement | null>) {
  const [height, setHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const fit = () => {
      const el = ref.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      setHeight(Math.max(window.innerWidth < 960 ? 520 : 560, Math.round(window.innerHeight - top - 8)));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [ref]);
  return height;
}

function useSize(ref: RefObject<HTMLElement | null>) {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setSize({ w: r.width, h: r.height });
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([e]) => setSize({ w: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

/** By area (A, mid, B), then the generic ones first, then by name. */
function byAreaThen<T>(get: (x: T) => { area: Area; importance: Stuff["importance"]; title: string }) {
  return (a: T, b: T) => {
    const x = get(a);
    const y = get(b);
    return AREA_ORDER.indexOf(x.area) - AREA_ORDER.indexOf(y.area) || importanceRank(x.importance) - importanceRank(y.importance) || x.title.localeCompare(y.title);
  };
}

/** Cards in groups, one per area when every area shows. */
function Grouped<T>({ items, areaOf, all, render }: { items: T[]; areaOf: (x: T) => Area; all: boolean; render: (x: T) => ReactNode }) {
  const { t } = useTranslation();
  if (!all) return <ul className="live-cards">{items.map(render)}</ul>;
  return (
    <>
      {AREA_ORDER.map((a) => {
        const these = items.filter((x) => areaOf(x) === a);
        if (these.length === 0) return null;
        return (
          <section key={a} className="live-group" aria-label={t(`area.${a}`)}>
            <h3 className="live-group-title">
              {t(`area.${a}`)} <span className="muted">{these.length}</span>
            </h3>
            <ul className="live-cards">{these.map(render)}</ul>
          </section>
        );
      })}
    </>
  );
}

/**
 * Live's board, filling the window: the radar of the map and side being played,
 * and beside it (under it, in a sheet, on a phone) the panel: the stuffs, combos
 * and executions of the players chosen, then the one picked, video first. With
 * the bomb down, one tap on its site turns the panel to the post-plant (T) or
 * the retakes (CT).
 */
export function LiveBoard({
  scope,
  map,
  side,
  players,
  present,
  members,
  me,
  header,
  party,
  bomb,
  dim,
}: {
  scope: string;
  map: MapId;
  side: Side | null;
  /** Whose stuffs show. */
  players: Set<string>;
  /** Who is in the game. */
  present: Set<string>;
  members: LiveMember[];
  me: string;
  /** The bar above: map, side, round, score. */
  header: ReactNode;
  /** Whose stuffs: mine, the game's, a choice. */
  party: ReactNode;
  /** The bomb is down, with this many seconds left. */
  bomb?: { left: number } | null;
  dim?: boolean;
}) {
  const { t } = useTranslation();
  const stuffs = useStuffs(scope, map);
  const combos = useCombos(scope, map);
  const tracked = useTracked(scope, map);
  const trackers = useTrackers(tracked.data);
  const executions = useExecutions(scope, map);
  const plants = usePlantSpots(scope, map);
  const narrow = useNarrow();
  const root = useRef<HTMLDivElement>(null);
  const main = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const listScroll = useRef(0);
  const height = useFill(root);
  const size = useSize(main);

  const [area, setArea] = useState<AreaFilter>("all");
  const [tab, setTab] = useState<Tab>("singles");
  const [open, setOpen] = useState<Open>(null);
  const [hot, setHot] = useState<string | null>(null);
  const [comboShow, setComboShow] = useState<{ id: string; show: ComboShow } | null>(null);
  const [execPick, setExecPick] = useState<{ id: string; actor: string | null; step: string | null } | null>(null);
  const [plantUtility, setPlantUtility] = useState<{ id: string; index: number | null } | null>(null);
  const [bombSite, setBombSite] = useState<Site | null>(null);
  const [bombAway, setBombAway] = useState(false);
  const [snap, setSnap] = useState<SheetSnap>("peek");
  const [level, setLevel] = useState<Level>("upper");

  const planted = !!bomb;
  // The bomb is gone (defused, blown, a new round): back to the utility.
  useEffect(() => {
    if (planted) return;
    setBombSite(null);
    setBombAway(false);
    setOpen((o) => (o?.kind === "plant" ? null : o));
  }, [planted]);
  // Sides switched: what was open belongs to the other side.
  useEffect(() => setOpen(null), [side]);
  // Nuke: B is downstairs.
  useEffect(() => {
    if (map === "de_nuke") setLevel(area === "b" ? "lower" : "upper");
  }, [area, map]);

  const singles = useMemo<Single[]>(
    () =>
      (stuffs.data ?? [])
        .filter((s) => (!side || s.side === side) && (area === "all" || s.area === area))
        .map((s) => ({ stuff: s, by: (trackers.get(s.id) ?? []).filter((id) => players.has(id)) }))
        .filter((x) => x.by.length > 0)
        .sort(byAreaThen((x) => x.stuff)),
    [stuffs.data, side, area, trackers, players],
  );
  // A combo shows when someone chosen tracks one of its throws.
  const comboItems = useMemo<ComboItem[]>(
    () =>
      (combos.data ?? [])
        .filter((c) => (!side || c.side === side) && (area === "all" || c.area === area) && comboSpot(c))
        .map((c) => ({ combo: c, by: [...new Set(c.steps.flatMap((s) => trackers.get(s.stuff.id) ?? []))].filter((id) => players.has(id)) }))
        .filter((x) => x.by.length > 0)
        .sort(byAreaThen((x) => x.combo)),
    [combos.data, side, area, trackers, players],
  );
  const execRows = useMemo<ExecRow[]>(
    () =>
      (executions.data ?? [])
        .filter((e) => (!side || e.side === side) && (area === "all" || e.target === area))
        .map((e) => execRow(e, present, me))
        .sort((a, b) => Number(b.ready) - Number(a.ready) || Number(!!b.mine) - Number(!!a.mine) || a.e.title.localeCompare(b.e.title)),
    [executions.data, side, area, present, me],
  );

  // The bomb: T get their post-plants on that site, CT their retakes.
  const bombMode = planted && bombSite !== null && !bombAway;
  const bombT = side !== "ct";
  const sitePlants = (plants.data ?? []).filter((p) => p.kind === "postplant" && p.site === bombSite);
  const siteRetakes = (executions.data ?? []).filter((e) => e.kind === "retake" && e.target === bombSite).map((e) => execRow(e, present, me));

  const openStuff = open?.kind === "stuff" ? ((stuffs.data ?? []).find((s) => s.id === open.id) ?? null) : null;
  const openCombo = open?.kind === "combo" ? ((combos.data ?? []).find((c) => c.id === open.id) ?? null) : null;
  const openExec = open?.kind === "exec" ? ((executions.data ?? []).find((e) => e.id === open.id) ?? null) : null;
  const openPlant = open?.kind === "plant" ? ((plants.data ?? []).find((p) => p.id === open.id) ?? null) : null;
  const { schedule, lookup } = usePlanSchedule(openExec?.plan ?? EMPTY_PLAN, openExec?.stuffs ?? NO_STUFFS, openExec?.combos ?? NO_COMBOS);
  const names = useActorNames(openExec ?? { members: [] });
  const execView = useMemo(() => (openExec ? focusOn(map, planPoints(openExec.plan, lookup), { maxScale: 2.6, pad: 0.1 }) : undefined), [map, openExec, lookup]);

  // An execution: my player first, and their first throw.
  const pick = openExec && execPick?.id === openExec.id ? execPick : null;
  const actor = openExec ? (openExec.plan.players.find((p) => p.id === pick?.actor) ?? openExec.plan.players.find((p) => p.memberId === me) ?? openExec.plan.players[0] ?? null) : null;
  const stepId = actor ? (pick?.actor === actor.id && pick.step ? pick.step : ((actor.actions.find((a) => a.kind === "throw" || a.kind === "combo") ?? actor.actions[0])?.id ?? null)) : null;
  const stepAction = actor?.actions.find((a) => a.id === stepId) ?? null;
  const stepTime = stepId ? (schedule.times.get(stepId)?.start ?? 0) : 0;
  const show: ComboShow = openCombo ? (comboShow?.id === openCombo.id ? comboShow.show : defaultComboShow(openCombo)) : 0;

  // The map follows what is open to its floor (Nuke).
  const openKey = open ? `${open.kind}:${open.id}` : "";
  useEffect(() => {
    if (openStuff) setLevel(stuffLevel(map, openStuff));
    else if (openCombo) {
      const spot = comboSpot(openCombo);
      if (spot) setLevel(levelAt(map, spot.z));
    } else if (openPlant) setLevel(levelAt(map, openPlant.plant.z ?? 0));
    else if (openExec) setLevel(floorOf(map, openExec.plan.players[0]?.start.z ?? null));
    // Only when another item opens: switching floors by hand stays.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openKey, map]);
  useEffect(() => {
    if (bombMode && bombSite && !openPlant) setLevel(siteBox(map, bombSite)?.level ?? "upper");
  }, [bombMode, bombSite, map, openPlant]);

  // An item opens at its top; back on the list, it is where it was. Another tab starts at the top.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = openKey ? 0 : listScroll.current;
  }, [openKey]);
  useLayoutEffect(() => {
    listScroll.current = 0;
    if (scroller.current) scroller.current.scrollTop = 0;
  }, [tab, bombMode]);

  // What previous / next go through: the list the open item came from.
  const order: { kind: NonNullable<Open>["kind"]; id: string }[] =
    open?.kind === "stuff"
      ? singles.map((x) => ({ kind: "stuff" as const, id: x.stuff.id }))
      : open?.kind === "combo"
        ? comboItems.map((x) => ({ kind: "combo" as const, id: x.combo.id }))
        : open?.kind === "exec"
          ? (bombMode ? siteRetakes : execRows).map((r) => ({ kind: "exec" as const, id: r.e.id }))
          : open?.kind === "plant"
            ? sitePlants.map((p) => ({ kind: "plant" as const, id: p.id }))
            : [];
  const at = order.findIndex((o) => o.kind === open?.kind && o.id === open?.id);

  const openItem = (o: NonNullable<Open>) => {
    if (!open) listScroll.current = scroller.current?.scrollTop ?? 0;
    setOpen(o);
    setHot(null);
    if (narrow && snap === "peek") setSnap("mid");
  };
  const go = (dir: -1 | 1) => {
    const next = order[at + dir];
    if (next) setOpen(next);
  };

  // Keys, for a second screen or a quick alt-tab: ← → go through the list, Escape goes back to it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || !open) return;
      // A viewer, the stuff drawer or a stack's list is open: the key is theirs.
      if (document.querySelector("dialog[open], [role=dialog]")) return;
      const el = e.target as HTMLElement | null;
      if (el?.closest?.("input, select, textarea, .clip-player, .zoom-shot-frame, [role=tablist], .live-sheet-handle")) return;
      if (e.key === "Escape") setOpen(null);
      else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        const next = order[at + (e.key === "ArrowLeft" ? -1 : 1)];
        if (!next) return;
        e.preventDefault();
        setOpen(next);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  // Still loading: the board's frame is there already (it measures itself), the rest comes.
  const waiting = stuffs.isPending || tracked.isPending || combos.isPending;

  const info = mapInfo(map);
  const nameOf = (id: string) => members.find((m) => m.id === id)?.name ?? "?";
  const listMode: Tab = open?.kind === "combo" ? "combos" : open?.kind === "stuff" ? "singles" : tab;

  // ---- the map
  let mapNode: ReactNode;
  if (openExec) {
    const s = stepAction?.kind === "throw" ? openExec.stuffs.find((x) => x.id === stepAction.stuffId) : undefined;
    const c = stepAction?.kind === "combo" ? openExec.combos.find((x) => x.id === stepAction.comboId) : undefined;
    mapNode = (
      <PlanStage map={map} mapName={info.name} level={level} plan={openExec.plan} schedule={schedule} elapsed={stepTime} focus={actor?.id ?? null} selected={null} view={execView} actorTitle={names.title} pickZ={null}>
        {s && (
          <>
            <ThrowLine map={map} from={s.throw} to={s.landing} side={s.side} />
            {levelAt(map, s.throw.z) === level && (
              <OnMap map={map} x={s.throw.x} y={s.throw.y} className="throw-spot">
                <span className={`throw-marker side-${s.side}`}>
                  <MarkerIcon kind="launch" grenade={s.grenade} side={s.side} selected />
                </span>
              </OnMap>
            )}
            {stuffLevel(map, s) === level && (
              <OnMap map={map} x={s.landing.x} y={s.landing.y}>
                <span className={`marker is-static side-${s.side}`}>
                  <MarkerIcon kind="impact" grenade={s.grenade} side={s.side} selected />
                </span>
              </OnMap>
            )}
          </>
        )}
        {c && <ComboOnMap map={map} combo={c} level={level} />}
      </PlanStage>
    );
  } else if (bombMode && bombT && bombSite) {
    const box = siteBox(map, bombSite);
    const focus = box ? focusOnBox({ u0: box.u0 - 0.1, v0: box.v0 - 0.1, u1: box.u1 + 0.1, v1: box.v1 + 0.1 }, { pad: 0, maxScale: 2.8 }) : undefined;
    mapNode = (
      <Radar map={map} mapName={info.name} level={level} focus={focus} onBackground={() => setOpen(null)}>
        <SiteOutline map={map} site={bombSite} level={level} active />
        {openPlant && <PlantOnMap map={map} plant={openPlant} level={level} layers={ALL_LAYERS} />}
        {sitePlants.map((p) =>
          levelAt(map, p.plant.z ?? 0) === level ? (
            <OnMap key={p.id} map={map} x={p.plant.x} y={p.plant.y}>
              <button
                type="button"
                className={`c4-marker${hot === `p:${p.id}` ? " is-hot" : ""}`}
                aria-pressed={openPlant?.id === p.id}
                aria-label={t("plant.c4Label", { name: p.name })}
                onClick={(e) => {
                  e.stopPropagation();
                  openItem({ kind: "plant", id: p.id });
                }}
              >
                <span className="c4-tag">C4</span>
                <span className="c4-name" aria-hidden="true">
                  {p.name}
                </span>
              </button>
            </OnMap>
          ) : null,
        )}
      </Radar>
    );
  } else {
    const markers: LiveMarker[] =
      listMode === "combos"
        ? comboItems
            .filter((x) => x.combo.id !== openCombo?.id && levelAt(map, comboSpot(x.combo)!.z) === level)
            .map((x) => {
              const spot = comboSpot(x.combo)!;
              return { key: `c${x.combo.id}`, ...worldToRadar(map, spot.x, spot.y), combo: x.combo, by: x.by };
            })
        : listMode === "singles"
          ? singles
              .filter((x) => x.stuff.id !== openStuff?.id && stuffLevel(map, x.stuff) === level)
              .map((x) => ({ key: `s${x.stuff.id}`, ...worldToRadar(map, x.stuff.landing.x, x.stuff.landing.y), stuff: x.stuff, by: x.by }))
          : [];
    const label = (m: LiveMarker) => `${m.stuff?.title ?? m.combo?.title ?? ""}, ${m.by.map(nameOf).join(", ")}`;
    const isHot = (m: LiveMarker) => hot === (m.stuff ? `s:${m.stuff.id}` : `c:${m.combo?.id}`);
    mapNode = (
      <Radar map={map} mapName={info.name} level={level} focus={areaFocus(map, area)} onBackground={() => setOpen(null)}>
        {openStuff && (
          <>
            <ThrowLine map={map} from={openStuff.throw} to={openStuff.landing} side={openStuff.side} />
            {levelAt(map, openStuff.throw.z) === level && (
              <OnMap map={map} x={openStuff.throw.x} y={openStuff.throw.y} className="throw-spot">
                <span className={`throw-marker side-${openStuff.side}`}>
                  <MarkerIcon kind="launch" grenade={openStuff.grenade} side={openStuff.side} selected />
                </span>
              </OnMap>
            )}
            {stuffLevel(map, openStuff) === level && (
              <OnMap map={map} x={openStuff.landing.x} y={openStuff.landing.y} className="is-picked">
                <button
                  type="button"
                  className={`marker live-marker is-picked side-${openStuff.side}`}
                  aria-pressed="true"
                  aria-label={openStuff.title}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(null);
                  }}
                >
                  <MarkerIcon kind="impact" grenade={openStuff.grenade} side={openStuff.side} selected />
                </button>
              </OnMap>
            )}
          </>
        )}
        {openCombo && <ComboOnMap map={map} combo={openCombo} level={level} active={typeof show === "number" ? show : null} onStep={(i) => setComboShow({ id: openCombo.id, show: i })} />}
        <StackedMarkers
          items={markers}
          isSelected={() => false}
          isHot={isHot}
          onSelect={(m) => openItem(m.stuff ? { kind: "stuff", id: m.stuff.id } : { kind: "combo", id: m.combo!.id })}
          renderOrigin={(m) => (m.stuff ? <MarkerIcon kind="launch" grenade={m.stuff.grenade} side={m.stuff.side} /> : null)}
          lineClass={(m) => (m.stuff ? `side-${m.stuff.side}` : undefined)}
          origin={(m) => (m.stuff && levelAt(map, m.stuff.throw.z) === level ? { ...worldToRadar(map, m.stuff.throw.x, m.stuff.throw.y), ...m.stuff.throw } : null)}
          label={label}
          renderGlyph={(m) => (m.stuff ? <GrenadeIcon grenade={m.stuff.grenade} side={m.stuff.side} size={22} /> : m.combo ? <ComboGlyphs combo={m.combo} size={9} /> : null)}
          renderPillGlyph={(m) => {
            const s = m.stuff ?? m.combo?.steps[0]?.stuff;
            return s ? <GrenadeMark grenade={s.grenade} side={m.stuff?.side ?? m.combo?.side ?? "t"} size={26} /> : null;
          }}
          kindOf={(m) => (m.stuff ? `${m.stuff.grenade}-${m.stuff.side}` : `combo-${m.combo?.steps[0]?.stuff.grenade}-${m.combo?.side}`)}
          sideOf={(m) => m.stuff?.side ?? m.combo?.side ?? "t"}
          renderMarker={(m) =>
            m.stuff ? (
              <button
                type="button"
                className={`marker live-marker side-${m.stuff.side}${isHot(m) ? " is-hot" : ""}`}
                aria-label={label(m)}
                onClick={(e) => {
                  e.stopPropagation();
                  openItem({ kind: "stuff", id: m.stuff!.id });
                }}
              >
                <MarkerIcon kind="impact" grenade={m.stuff.grenade} side={m.stuff.side} />
                <span className="live-marker-who">
                  {initial(nameOf(m.by[0] ?? ""))}
                  {m.by.length > 1 ? `+${m.by.length - 1}` : ""}
                </span>
              </button>
            ) : m.combo ? (
              <button
                type="button"
                className={`marker combo-marker side-${m.combo.side}${isHot(m) ? " is-hot" : ""}`}
                aria-label={t("combo.marker", { title: m.combo.title, count: m.combo.steps.length })}
                title={m.combo.title}
                onClick={(e) => {
                  e.stopPropagation();
                  openItem({ kind: "combo", id: m.combo!.id });
                }}
              >
                <ComboGlyphs combo={m.combo} />
              </button>
            ) : null
          }
        />
      </Radar>
    );
  }

  // ---- the panel
  const head = (back: string) => ({ back, onBack: () => setOpen(null), nav: at >= 0 ? { at, count: order.length, go } : undefined });
  const hotOf = (key: string) => ({ hot: hot === key, onHot: (on: boolean) => setHot(on ? key : (h) => (h === key ? null : h)) });
  let body: ReactNode;
  if (openStuff) {
    body = <StuffView stuff={openStuff} by={(trackers.get(openStuff.id) ?? []).filter((id) => players.has(id))} members={members} head={head(t("live.back.singles"))} />;
  } else if (openCombo) {
    const by = [...new Set(openCombo.steps.flatMap((s) => trackers.get(s.stuff.id) ?? []))].filter((id) => players.has(id));
    body = <ComboView combo={openCombo} by={by} members={members} show={show} onShow={(s) => setComboShow({ id: openCombo.id, show: s })} head={head(t("live.back.combos"))} />;
  } else if (openExec) {
    body = (
      <ExecView
        map={map}
        execution={openExec}
        row={execRow(openExec, present, me)}
        me={me}
        actor={actor}
        onActor={(id) => setExecPick({ id: openExec.id, actor: id, step: null })}
        step={stepId}
        onStep={(id) => setExecPick({ id: openExec.id, actor: actor?.id ?? null, step: id })}
        schedule={schedule}
        lookup={lookup}
        head={head(bombMode ? t("live.back.retakes", { site: t(`areaShort.${bombSite}`) }) : t("live.back.executions"))}
      />
    );
  } else if (openPlant) {
    body = (
      <PlantView
        plant={openPlant}
        me={me}
        left={bomb?.left ?? null}
        pickedUtility={plantUtility?.id === openPlant.id ? plantUtility.index : null}
        onUtility={(i) => setPlantUtility({ id: openPlant.id, index: i })}
        head={head(t("live.back.postPlant", { site: t(`areaShort.${openPlant.site}`) }))}
      />
    );
  } else if (bombMode && bombSite) {
    const siteName = t(`area.${bombSite}`);
    body = (
      <div className="live-list">
        <h2 className="live-list-title">{bombT ? t("live.postPlantOn", { site: siteName }) : t("live.retakesOn", { site: siteName })}</h2>
        <ul className="live-cards">
          {bombT
            ? sitePlants.map((p) => (
                <li key={p.id}>
                  <PlantCard plant={p} me={me} onOpen={() => openItem({ kind: "plant", id: p.id })} {...hotOf(`p:${p.id}`)} />
                </li>
              ))
            : siteRetakes.map((r) => (
                <li key={r.e.id}>
                  <ExecCard row={r} onOpen={() => openItem({ kind: "exec", id: r.e.id })} {...hotOf(`e:${r.e.id}`)} />
                </li>
              ))}
        </ul>
        {(bombT ? sitePlants.length : siteRetakes.length) === 0 && (
          <div className="live-empty-list">
            <p className="muted">{bombT ? t("live.noPostPlant", { site: siteName }) : t("live.noRetakes", { site: siteName })}</p>
            <Link to={bombT ? `/library/${map}/post-plant?site=${bombSite}` : `/library/${map}/executions?k=retake`}>{bombT ? t("live.postPlant") : t("live.openRetakes")}</Link>
          </div>
        )}
      </div>
    );
  } else {
    const counts: Record<Tab, number> = { singles: singles.length, combos: comboItems.length, executions: execRows.length };
    body = (
      <>
        <div className="live-list-head">
          {party}
          <div className="segmented live-tabs" role="group" aria-label={t("live.showWhat")}>
            {(["singles", "combos", "executions"] as const).map((k) => (
              <button key={k} type="button" className="seg" aria-pressed={tab === k} onClick={() => setTab(k)}>
                {t(`live.tabs.${k}`)}
                <span className="seg-count">{counts[k]}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="live-list">
          {tab === "singles" && (
            <Grouped
              items={singles}
              all={area === "all"}
              areaOf={(x) => x.stuff.area}
              render={(x) => (
                <li key={x.stuff.id}>
                  <StuffCard stuff={x.stuff} by={x.by} members={members} me={me} onOpen={() => openItem({ kind: "stuff", id: x.stuff.id })} {...hotOf(`s:${x.stuff.id}`)} />
                </li>
              )}
            />
          )}
          {tab === "combos" && (
            <Grouped
              items={comboItems}
              all={area === "all"}
              areaOf={(x) => x.combo.area}
              render={(x) => (
                <li key={x.combo.id}>
                  <ComboCard combo={x.combo} by={x.by} members={members} me={me} onOpen={() => openItem({ kind: "combo", id: x.combo.id })} {...hotOf(`c:${x.combo.id}`)} />
                </li>
              )}
            />
          )}
          {tab === "executions" && (
            <ul className="live-cards">
              {execRows.map((r) => (
                <li key={r.e.id}>
                  <ExecCard row={r} onOpen={() => openItem({ kind: "exec", id: r.e.id })} {...hotOf(`e:${r.e.id}`)} />
                </li>
              ))}
            </ul>
          )}
          {counts[tab] === 0 && (
            <div className="live-empty-list">
              <p className="muted">
                {tab === "singles"
                  ? t("live.nothingTracked", { side: side ? t(`live.side.${side}`) : "" })
                  : tab === "combos"
                    ? t("live.noCombos", { side: side ? t(`live.side.${side}`) : "" })
                    : t("live.noExecs", { side: side ? t(`live.side.${side}`) : "" })}
              </p>
              <Link to={tab === "executions" ? `/library/${map}/executions` : `/library/${map}${tab === "combos" ? "?type=combos" : ""}`}>{t("live.openLibrary", { map: info.name })}</Link>
            </div>
          )}
        </div>
        {!narrow && counts[tab] > 0 && <p className="live-keys small muted">{t("live.keys")}</p>}
      </>
    );
  }
  const panel = (
    <>
      {bomb && (
        <BombStrip
          left={bomb.left}
          site={bombSite}
          onSite={(s) => {
            setBombSite(s);
            setBombAway(false);
            setOpen(null);
          }}
          away={bombAway}
          onAway={(away) => {
            setBombAway(away);
            setOpen(null);
          }}
        />
      )}
      <div className="live-panel-body" ref={scroller}>{stuffs.error ? <ErrorState message={errorText(t, stuffs.error)} onRetry={() => void stuffs.refetch()} /> : waiting ? <Loading /> : body}</div>
    </>
  );

  // The sheet's three heights: the map whole above it, about two thirds up, nearly all the way.
  const H = size.h || 640;
  const W = size.w || 360;
  const peek = Math.max(170, Math.round(H - W));
  const full = Math.max(peek + 60, Math.round(H - 110));
  const heights: Record<SheetSnap, number> = { peek, mid: Math.min(full - 40, Math.max(peek + 40, Math.round(H * 0.66))), full };
  const picked = openStuff || openCombo;

  return (
    <div ref={root} className={`live-cockpit${narrow ? " is-narrow" : ""}`} style={height ? { height } : undefined}>
      {header}
      <div ref={main} className="live-main">
        <div className="live-map-wrap" style={narrow ? { bottom: heights[snap] } : undefined}>
          <section className={`map-frame live-map${dim ? " is-stale" : ""}${picked ? " has-pick" : ""}`} aria-label={info.name}>
            {waiting ? <Loading /> : mapNode}
            {!openExec && !bombMode && (
              <div className="map-overlay map-overlay-top">
                <div className="segmented glass" role="group" aria-label={t("live.area")}>
                  {(["all", "a", "b", "mid"] as const).map((a) => (
                    <button key={a} type="button" className="seg" aria-pressed={area === a} onClick={() => setArea(a)}>
                      {t(`areaShort.${a}`)}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {hasLowerLevel(map) && (
              <div className="map-overlay map-overlay-bottom glass">
                <div className="segmented" role="group" aria-label={t("level.label")}>
                  {(["upper", "lower"] as const).map((l) => (
                    <button key={l} type="button" className="seg" aria-pressed={level === l} onClick={() => setLevel(l)}>
                      {t(`level.${l}`)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>
        {narrow ? (
          <LiveSheet snap={snap} onSnap={setSnap} heights={heights} label={t("live.panel")}>
            {panel}
          </LiveSheet>
        ) : (
          <aside className="live-panel card" aria-label={t("live.panel")}>
            {panel}
          </aside>
        )}
      </div>
    </div>
  );
}
